// Per-area D2D coverage units (Open Technical Constraint T8). US carrier routes carry
// HOME_COUNT/APT_COUNT on the feature, so their units come straight off the boundary.
// Pathfinder's MissionBoundaries ship `addresses: 0` (searchFeatures never counts), so
// every other country asks findNumberHouseholds — the same source api prices D2D from.
// Kept free of map/auth imports so it runs under the standalone node jest config.

// Units read off a boundary feature's properties; authoritative for US carrier routes.
export const deriveUnits = (metadata: any): number => {
  if (!metadata) return 0;
  const home = Number(metadata.HOME_COUNT) || 0;
  const apt = Number(metadata.APT_COUNT) || 0;
  if (home || apt) return home + apt;
  return Number(metadata.addressCount ?? metadata.deliveryPointCount ?? metadata.count) || 0;
};

// Aliased findNumberHouseholds fields per request — keeps each pathfinder call small.
export const HOUSEHOLDS_CHUNK = 20;

// A non-US area still needs its household count when it has no positive units yet.
export const needsHouseholds = (countryCode: string, area: { units?: number }): boolean =>
  countryCode !== "US" && !(typeof area.units === "number" && area.units > 0);

export type HouseholdsCall = (
  query: string,
  variables: Record<string, unknown>
) => Promise<{ json: () => Promise<{ data?: Record<string, { count?: unknown } | null> }> }>;

const buildQuery = (size: number): string => {
  const defs = Array.from({ length: size }, (_, i) => `$g${i}: GeoJSONGeometryInput`).join(", ");
  const fields = Array.from(
    { length: size },
    (_, i) => `  h${i}: findNumberHouseholds(countryCode: $countryCode, geometry: $g${i}) { count }`
  ).join("\n");
  return `query($countryCode: String!, ${defs}) {\n${fields}\n}`;
};

// Household counts by area id for non-US D2D areas; US (and areas without geometry) are skipped.
export const fetchAreaHouseholds = async ({
  countryCode,
  areas,
  call,
}: {
  countryCode: string;
  areas: Array<{ id: string; geometry?: unknown }>;
  call: HouseholdsCall;
}): Promise<Map<string, number>> => {
  const counts = new Map<string, number>();
  if (!countryCode || countryCode === "US") return counts;
  const withGeometry = areas.filter((a) => a.geometry);

  for (let start = 0; start < withGeometry.length; start += HOUSEHOLDS_CHUNK) {
    const chunk = withGeometry.slice(start, start + HOUSEHOLDS_CHUNK);
    const variables: Record<string, unknown> = { countryCode };
    chunk.forEach((a, i) => {
      variables[`g${i}`] = a.geometry;
    });
    const response = await call(buildQuery(chunk.length), variables);
    const { data } = await response.json();
    chunk.forEach((a, i) => {
      const count = data?.[`h${i}`]?.count;
      if (typeof count === "number") counts.set(a.id, count);
    });
  }
  return counts;
};
